import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Car, CheckCircle, ArrowLeft } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import GoogleReviews from '../components/GoogleReviews';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { useToast } from '../hooks/use-toast';
import { useAPI, useAPIMutation } from '../hooks/useAPI';
import { locationsAPI, contactsAPI } from '../services/api';
import { phoneToWhatsAppUrl, emailToMailto } from '../lib/contact';
import emailjs from '@emailjs/browser';

// Configuration EmailJS pour le formulaire de contact
const EMAILJS_SERVICE_ID = 'service_t96nhl9';
const EMAILJS_TEMPLATE_ID = 'template_dc7shhh';
const EMAILJS_PUBLIC_KEY = 'fcFKtJepci4Bk3alr';

const ContactPage = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  
  // Pré-remplir le sujet si on vient d'un bouton "Demande de devis"
  const initialSubject = searchParams.get('sujet') === 'devis' ? 'Demande de devis' : '';
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: initialSubject,
    message: '',
    location: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Mettre à jour le sujet si le paramètre URL change
  useEffect(() => {
    if (searchParams.get('sujet') === 'devis') {
      setFormData(prev => ({ ...prev, subject: 'Demande de devis' }));
    }
  }, [searchParams]);

  // API calls
  const { 
    data: locations, 
    loading: locationsLoading, 
    error: locationsError 
  } = useAPI(() => locationsAPI.getAll());

  // Mutation pour créer le message de contact
  const { mutate: createContact, loading: contactLoading } = useAPIMutation();

  const subjects = [
    'Demande de devis',
    'Prise de rendez-vous',
    'Renseignements services',
    'Réclamation',
    'Autre'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.message.trim() || !formData.subject) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Envoyer via l'API backend
      const contactData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject,
        message: formData.message,
        locationId: formData.location || null
      };

      try {
        await createContact(() => contactsAPI.create(contactData));
      } catch (contactError) {
        console.error('Erreur enregistrement message:', contactError);
        toast({
          title: "Erreur",
          description: "Impossible d'envoyer le message. Veuillez réessayer ou nous contacter directement par téléphone.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      
      // Trouver le nom du centre si sélectionné
      const selectedLocation = locations?.find(loc => loc.id === formData.location);
      const locationName = selectedLocation ? selectedLocation.name : 'Non spécifié';
      
      // Envoyer notification email via EmailJS
      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        from_phone: formData.phone || 'Non renseigné',
        subject: formData.subject,
        location: locationName,
        message: formData.message,
        date_received: new Date().toLocaleString('fr-FR', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      };

      // Le message est déjà enregistré : un échec d'envoi ne doit pas bloquer le client.
      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          templateParams,
          EMAILJS_PUBLIC_KEY
        );
      } catch (emailError) {
        console.error("Échec de l'envoi de l'email de contact:", emailError);
      }

      // Afficher le message de succès
      setSubmitSuccess(true);
      
      // Réinitialiser le formulaire
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        location: ''
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Erreur", 
        description: "Impossible d'envoyer le message. Veuillez réessayer ou nous contacter directement par téléphone.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_detailing-portal/artifacts/4bgvvezy_Black%20and%20Blue%20Modern%20Automotive%20Carwash%20Logo.avif" 
                alt="AutoSpa 77 - Detailing Automobile"
                className="h-64 w-auto"
                onError={(e) => {
                  // Fallback si l'image ne charge pas
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl" style={{display: 'none'}}>
                <Car className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Contactez AutoSpa 77
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Nous sommes à votre disposition pour répondre à toutes vos questions 
              et vous accompagner dans vos projets de detailing automobile.
            </p>
            <div className="flex items-center justify-center space-x-8 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold">{locations?.length || 3}</div>
                <div className="text-gray-300">Centres en 77</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">24h</div>
                <div className="text-gray-300">Délai de réponse</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">10+</div>
                <div className="text-gray-300">Ans d'expérience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Send className="w-6 h-6 text-blue-600" />
                    <span>Envoyez-nous un message</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Message de succès */}
                  {submitSuccess ? (
                    <div className="text-center py-8">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-12 h-12 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        Message envoyé avec succès !
                      </h3>
                      <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        Merci pour votre message. Nous avons bien reçu votre demande et nous vous répondrons dans les plus brefs délais (généralement sous 24h).
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
                        <p className="text-blue-800 text-sm">
                          <strong>Besoin d'une réponse urgente ?</strong><br />
                          Appelez-nous directement au <a href="tel:0622360190" className="font-bold underline">06 22 36 01 90</a>
                        </p>
                      </div>
                      <Button 
                        onClick={() => setSubmitSuccess(false)}
                        variant="outline"
                        className="mt-2"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Envoyer un autre message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="firstName">Prénom *</Label>
                          <Input
                            id="firstName"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            required
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="lastName">Nom *</Label>
                          <Input
                            id="lastName"
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            required
                            className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Téléphone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="subject">Sujet *</Label>
                        <select 
                          id="subject"
                          value={formData.subject} 
                          onChange={(e) => handleInputChange('subject', e.target.value)}
                          required
                          className="mt-1 flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="">Choisir un sujet</option>
                          {subjects.map((subject) => (
                            <option key={subject} value={subject}>
                              {subject}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="location">Centre concerné</Label>
                        <select 
                          id="location"
                          value={formData.location} 
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          className="mt-1 flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="">Choisir un centre</option>
                          {locationsLoading ? (
                            <option value="" disabled>Chargement...</option>
                          ) : locations ? (
                            locations.map((location) => (
                              <option key={location.id} value={location.id}>
                                {location.name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>Aucun centre disponible</option>
                          )}
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        required
                        placeholder="Décrivez votre demande..."
                        className="mt-1"
                        rows={6}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isSubmitting ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Envoyer le message
                        </>
                      )}
                    </Button>
                  </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              {/* Quick Contact */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Phone className="w-6 h-6 text-blue-600" />
                    <span>Contact direct</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-semibold">Téléphone / WhatsApp</p>
                      <a
                        href={phoneToWhatsAppUrl('0622360190')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:underline"
                        data-testid="contact-phone-whatsapp"
                      >
                        06 22 36 01 90
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-semibold">Email</p>
                      <a href={emailToMailto('contact.autospa77@gmail.com')} className="text-blue-600 hover:underline" data-testid="contact-email-link">
                        contact.autospa77@gmail.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Locations */}
              <div className="space-y-4">
                {locationsLoading ? (
                  <div className="text-center py-4">
                    <LoadingSpinner />
                    <p className="mt-2 text-gray-600">Chargement des adresses...</p>
                  </div>
                ) : locationsError ? (
                  <ErrorMessage error={locationsError} />
                ) : locations ? (
                  locations.map((location) => (
                    <Card key={location.id}>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">{location.name}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-3">
                        <div className="flex items-start space-x-3">
                          <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Adresse</p>
                            <p className="text-sm text-gray-600">{location.address}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">Horaires</p>
                            <div className="text-sm text-gray-600 space-y-1">
                              {Object.entries(location.hours).map(([day, hours]) => (
                                <div key={day} className="flex justify-between">
                                  <span>{day}:</span>
                                  <span className="font-medium">{hours}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="pt-3 border-t border-gray-100 space-y-2">
                          <a
                            href={phoneToWhatsAppUrl(location.phone)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-sm text-green-600 hover:underline"
                            data-testid={`location-phone-${location.id}`}
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {location.phone}
                          </a>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`, '_blank')}
                            className="w-full text-blue-600 border-blue-600 hover:bg-blue-50"
                            data-testid={`location-map-${location.id}`}
                          >
                            <MapPin className="w-4 h-4 mr-2" />
                            Voir sur la carte
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    Aucune adresse disponible
                  </div>
                )}
              </div>

              {/* FAQ Quick Links */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Questions fréquentes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="font-medium">Combien de temps pour un service ?</p>
                      <p className="text-gray-600">De 45 min à plusieurs jours selon le service</p>
                    </div>
                    <div>
                      <p className="font-medium">Faut-il prendre rendez-vous ?</p>
                      <p className="text-gray-600">Recommandé pour garantir votre créneau</p>
                    </div>
                    <div>
                      <p className="font-medium">Acceptez-vous les cartes ?</p>
                      <p className="text-gray-600">Espèces, CB et virements acceptés</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Avis Google Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              L'avis de nos clients
            </h2>
            <p className="text-lg text-gray-600">
              Découvrez pourquoi nos clients nous font confiance
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <GoogleReviews 
              locationName="AutoSpa 77 - Ozoir-la-Ferrière"
              address="8 Rue Lavoisier, 77330 Ozoir-la-Ferrière"
              compact={true}
            />
            <GoogleReviews 
              locationName="AutoSpa 77 - Croissy-Beaubourg"
              address="56 Boulevard Courcerin, 77183 Croissy-Beaubourg"
              compact={true}
            />
          </div>
        </div>
      </section>

      {/* Section Carte */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nos Centres AutoSpa 77
            </h2>
            <p className="text-lg text-gray-600">
              Plusieurs adresses pour mieux vous servir en Seine-et-Marne
            </p>
          </div>

          <div className="rounded-xl overflow-hidden shadow-lg h-96 mb-8">
            <iframe
              title="Carte des centres AutoSpa 77"
              src={`https://www.google.com/maps?q=${encodeURIComponent(locations?.[0]?.address || 'Seine-et-Marne, France')}&z=10&output=embed`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-map-iframe"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {locations?.map((location) => (
              <button
                key={location.id}
                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`, '_blank')}
                className="group flex items-start gap-3 rounded-xl bg-white p-5 text-left shadow-sm ring-1 ring-gray-100 transition-all duration-200 hover:shadow-md hover:ring-blue-200"
                data-testid={`map-center-${location.id}`}
              >
                <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                  <MapPin className="w-4 h-4" />
                </span>
                <span>
                  <span className="block font-semibold text-gray-900">
                    {location.name?.replace('AutoSpa 77 - ', '')}
                  </span>
                  <span className="block text-sm text-gray-600">{location.address}</span>
                  <span className="mt-1 inline-block text-sm font-medium text-blue-600 group-hover:underline">
                    Voir l'itinéraire →
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;